import React, { useState } from 'react'
import * as AntD from 'antd'
import * as AntDIcons from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

const { Button, Form, Input, InputNumber, Select, Empty, Alert, Spin } = AntD


export default function ExecutionPanel({ workflow, services }) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [executing, setExecuting] = useState(false)
  const [renderOutput, setRenderOutput] = useState(null)
  const [error, setError] = useState(null)
  const [renderKey, setRenderKey] = useState(0)  // For resetting error boundary

  const uiConfig = workflow?.ui_config || {}
  const inputs = uiConfig.inputs || []

  // Execute the script
  const handleExecute = () => {
    const values = form.getFieldsValue()
    const script = workflow?.script || ''

    if (!script.trim()) {
      setError(t('workflows.no_script'))
      return
    }

    setExecuting(true)
    setError(null)
    setRenderOutput({ script, inputs: values })
    setRenderKey(k => k + 1)
  }

  // Set default values when workflow changes
  React.useEffect(() => {
    const defaults = {}
    inputs.forEach(input => {
      if (input.default !== undefined) {
        defaults[input.name] = input.default
      }
    })
    form.resetFields()
    form.setFieldsValue(defaults)
    setRenderOutput(null)
    setError(null)
  }, [workflow, form])

  // Render input form based on ui_config.inputs
  const renderInputForm = () => {
    if (inputs.length === 0) {
      return null
    }

    return (
      <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
        {inputs.map((input, idx) => (
          <Form.Item key={idx} label={input.label} name={input.name}>
            {renderInputField(input)}
          </Form.Item>
        ))}
      </Form>
    )
  }

  const renderInputField = (input) => {
    switch (input.type) {
      case 'number':
        return <InputNumber style={{ width: '100%' }} />
      case 'select':
        return (
          <Select
            options={(input.options || []).map(o =>
              typeof o === 'object' ? o : { value: o, label: o }
            )}
          />
        )
      case 'textarea':
        return <Input.TextArea rows={3} />
      default:
        return <Input />
    }
  }


  // Render output area
  const renderResult = () => {
    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message={t('workflows.script_error')}
          description={
            <pre style={{
              fontSize: 12,
              maxHeight: 200,
              overflow: 'auto',
              background: '#fff1f0',
              padding: 8,
              borderRadius: 4,
              margin: '8px 0 0 0',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {error}
            </pre>
          }
        />
      )
    }

    if (!renderOutput) {
      return <Empty description={t('workflows.no_result')} />
    }

    return <Empty description={t('workflows.no_result')} />
  }

  return (
    <div>
      {renderInputForm()}

      <Button
        type="primary"
        icon={<AntDIcons.PlayCircleOutlined />}
        onClick={handleExecute}
        loading={executing}
        style={{ marginBottom: 16 }}
      >
        {t('workflows.execute')}
      </Button>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{t('workflows.result')}</div>
        {renderResult()}
      </div>
    </div>
  )
}
